import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
	AbstractControl,
	FormArray,
	FormBuilder,
	FormGroup,
	ReactiveFormsModule,
} from '@angular/forms';
import { TranslocoModule } from '@ngneat/transloco';
import { CountryDto } from '@splitsecondconnect/ultramarine-shared';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

import { BaseSectionComponent } from '../../../../../../shared/base-classes/sections/base-section.component';
import { SectionWrapperComponent } from '../../../../../../shared/components/containers/section-wrapper-container/section-wrapper-container.component';
import { StackItemContainerComponent } from '../../../../../../shared/components/containers/stack-item-container/stack-item-container.component';
import { DatetimePickerComponent } from '../../../../../../shared/components/datetime-picker/datetime-picker.component';
import { SingleDropdownComponent } from '../../../../../../shared/components/dropdowns/single-dropdown/single-dropdown.component';
import { InputFieldComponent } from '../../../../../../shared/components/input-field/input-field.component';
import { MoreSectionsComponent } from '../../../../../../shared/components/more-sections/more-sections.component';
import { ToggleNewComponent } from '../../../../../../shared/components/toggle-new/toggle-new.component';
import { UltraPrimaryButtonComponent } from '../../../../../../shared/components/ultramarine-ui/ultra-primary/ultra-primary.component';
import {
	dateCast,
	toDateString,
} from '../../../../../../shared/functions/sectionCasters.function';
import { DriverFlatModel } from '../../../../../../shared/models/driver-flat.model';
import { SettingsService } from '../../../../settings/services/settings.service';
import { IDENTITY_DOCUMENT_TYPES } from '../../../translations/customers-select-items';

interface DrivingLicenseFormValue {
	id?: number | null;
	number?: string | null;
	category?: string | null;
	issueDate?: Date | string | null;
	expiryDate?: Date | string | null;
	issuedByCountryId?: number | null;
	isPrimary?: boolean;
}

/**
 * Renders the driver section of a customer record including identity
 * document fields and a dynamic list of driving licences, with exactly
 * one licence required to be flagged as primary at any time
 */
@Component({
	selector: 'app-customers-driver',
	templateUrl: 'customers-driver.component.html',
	imports: [
		ReactiveFormsModule,
		TranslocoModule,
		InputFieldComponent,
		SingleDropdownComponent,
		DatetimePickerComponent,
		SectionWrapperComponent,
		StackItemContainerComponent,
		MoreSectionsComponent,
		ToggleNewComponent,
		UltraPrimaryButtonComponent,
	],
})
export class CustomersDriverComponent
	extends BaseSectionComponent<DriverFlatModel>
	implements OnInit, OnDestroy
{
	countries = inject(SettingsService).countriesSelectItems;
	protected _skipCount: number = 0;
	public countries$: BehaviorSubject<CountryDto[]> = new BehaviorSubject<
		CountryDto[]
	>(null);

	public customerIdentityDocumentTypes = IDENTITY_DOCUMENT_TYPES;

	/**
	 * Stable keys per FormGroup for trackBy, so the list does not remount
	 * rows when licences are added, removed, or reordered
	 */
	#licenseKeyCounter = 0;
	#licenseKeys = new WeakMap<FormGroup, number>();

	/**
	 * Guard that prevents the primary mutex from recursively firing while
	 * it demotes the previous primary
	 */
	#updatingPrimary = false;

	/**
	 * Notifier used to tear down per-licence valueChanges subscriptions so
	 * they can be rebuilt cleanly whenever the licence array mutates
	 */
	#primarySubs$ = new Subject<void>();

	constructor(private _formBuilder: FormBuilder) {
		super();
		this.detailsForm = this._formBuilder.group({
			identityDocumentType: [null],
			identityDocumentNumber: [null],
			identityDocumentIssueDate: [null],
			identityDocumentExpiryDate: [null],
			identityDocumentIssuedByCountryId: [null],
			drivingLicenses: this._formBuilder.array([
				this._createDrivingLicenseForm(),
			]),
		});
	}

	public get drivingLicenses(): FormArray<FormGroup> {
		return this.detailsForm.controls.drivingLicenses as FormArray<FormGroup>;
	}

	/**
	 * Returns a stable key for each licence FormGroup so the template
	 * preserves focus and DOM state when the array changes
	 */
	public licenseTrackBy(_index: number, control: AbstractControl): number {
		const group = control as FormGroup;
		let key = this.#licenseKeys.get(group);

		if (key === undefined) {
			key = this.#licenseKeyCounter++;
			this.#licenseKeys.set(group, key);
		}

		return key;
	}

	ngOnDestroy(): void {
		this.#primarySubs$.next();
		this.#primarySubs$.complete();
	}

	public addDrivingLicense(): void {
		this.drivingLicenses.push(this._createDrivingLicenseForm());
		this.drivingLicenses.markAsDirty();
		this._subscribeToPrimaryChanges();
	}

	/**
	 * Removes a licence, blocking the last one to keep at least a single
	 * row, and promotes the first remaining licence to primary when the
	 * primary was the one being removed
	 */
	public removeDrivingLicense(control: AbstractControl): void {
		const index = this.drivingLicenses.controls.indexOf(control as FormGroup);

		if (index < 0) {
			return;
		}

		if (this.drivingLicenses.length <= 1) {
			return;
		}

		const isPrimary = Boolean(
			this.drivingLicenses.at(index)?.controls.isPrimary.value,
		);

		this.drivingLicenses.removeAt(index);
		this.drivingLicenses.markAsDirty();

		if (isPrimary && this.drivingLicenses.length > 0) {
			this._setPrimaryDrivingLicense(0);
		}

		this._subscribeToPrimaryChanges();
	}

	/**
	 * Wires the mutex that keeps a single primary licence, tearing down any
	 * previous subscriptions first so captured references stay fresh after
	 * the array has been mutated
	 *
	 * Turning a licence on demotes every other entry, turning the current
	 * primary off auto-promotes the first remaining licence so the list is
	 * never left without a primary
	 *
	 * Source and sibling groups are matched by reference rather than by
	 * index because positional indices go stale across add and remove
	 */
	private _subscribeToPrimaryChanges(): void {
		this.#primarySubs$.next();

		this.drivingLicenses.controls.forEach((sourceGroup) => {
			sourceGroup.controls.isPrimary.valueChanges
				.pipe(takeUntil(this.#primarySubs$))
				.subscribe((value) => {
					if (this.#updatingPrimary) {
						return;
					}

					this.#updatingPrimary = true;

					if (value) {
						this.drivingLicenses.controls.forEach((otherGroup) => {
							if (otherGroup !== sourceGroup) {
								otherGroup.controls.isPrimary.setValue(false, {
									emitEvent: false,
								});
							}
						});
					} else {
						const nextPrimaryGroup = this.drivingLicenses.controls.find(
							(group) => group !== sourceGroup,
						);

						nextPrimaryGroup?.controls.isPrimary.setValue(true, {
							emitEvent: false,
						});
					}

					this.#updatingPrimary = false;
				});
		});
	}

	/**
	 * Hydrates the form from the record, falling back to the legacy flat
	 * single-licence fields on the driver model when no licence array is
	 * present, and guarantees at least one licence is marked primary
	 */
	protected _mapModelToForm(): void {
		const record: DriverFlatModel = this.recordData();

		const detailsFormData: any = {
			identityDocumentType: record.driverIdentityDocumentType,
			identityDocumentNumber: record.driverIdentityDocumentNumber,
			identityDocumentIssueDate: toDateString(
				record.driverIdentityDocumentIssueDate,
			),
			identityDocumentExpiryDate: toDateString(
				record.driverIdentityDocumentExpiryDate,
			),
			identityDocumentIssuedByCountryId:
				record.driverIdentityDocumentIssuedByCountryId,
		};

		this.detailsForm.patchValue(detailsFormData, {
			emitEvent: false,
		});

		const normalizedDrivingLicenses: DrivingLicenseFormValue[] = Array.isArray(
			record.driverDrivingLicenses,
		)
			? record.driverDrivingLicenses.map((license) => ({
					id: license.id ?? null,
					number: license.number ?? null,
					category: license.category ?? null,
					issueDate: license.issueDate ?? null,
					expiryDate: license.expiryDate ?? null,
					issuedByCountryId: license.issuedByCountryId ?? null,
					isPrimary: Boolean(license.isPrimary),
				}))
			: [];

		const fallbackDrivingLicense: DrivingLicenseFormValue = {
			id: null,
			number: record.driverDrivingLicenseNumber ?? null,
			category: record.driverDrivingLicenseCategory ?? null,
			issueDate: record.driverDrivingLicenseIssueDate ?? null,
			expiryDate: record.driverDrivingLicenseExpiryDate ?? null,
			issuedByCountryId: record.driverDrivingLicenseIssuedByCountryId ?? null,
			isPrimary: true,
		};

		const drivingLicenses =
			normalizedDrivingLicenses.length > 0
				? normalizedDrivingLicenses
				: [fallbackDrivingLicense];

		this.detailsForm.setControl(
			'drivingLicenses',
			this._formBuilder.array(
				drivingLicenses.map((license) =>
					this._createDrivingLicenseForm({
						id: license.id ?? null,
						number: license.number ?? null,
						category: license.category ?? null,
						issueDate: toDateString(dateCast(license.issueDate)),
						expiryDate: toDateString(dateCast(license.expiryDate)),
						issuedByCountryId: license.issuedByCountryId ?? null,
						isPrimary: Boolean(license.isPrimary),
					}),
				),
			),
			{
				emitEvent: false,
			},
		);

		if (this.drivingLicenses.length > 0) {
			const hasPrimary = this.drivingLicenses.controls.some((license) =>
				Boolean(license.controls.isPrimary.value),
			);

			if (!hasPrimary) {
				this._setPrimaryDrivingLicense(0, false);
			}
		}

		this._subscribeToPrimaryChanges();

		if (!this.isEditMode) {
			this.detailsForm.disable({ emitEvent: false });
		}
	}

	/**
	 * Projects the form state back onto the driver model, emitting the full
	 * licence array and mirroring the primary licence onto the legacy flat
	 * fields that downstream consumers still rely on
	 */
	protected _mapFormToModel(): Partial<DriverFlatModel> {
		const drivingLicenses = this.drivingLicenses.controls.map((license) => ({
			id: license.controls.id.value,
			number: license.controls.number.value,
			category: license.controls.category.value,
			issueDate: license.controls.issueDate.value,
			expiryDate: license.controls.expiryDate.value,
			issuedByCountryId: license.controls.issuedByCountryId.value,
			isPrimary: Boolean(license.controls.isPrimary.value),
		}));

		const primaryDrivingLicense =
			drivingLicenses.find((license) => license.isPrimary) ??
			drivingLicenses[0] ??
			null;

		return {
			driverIdentityDocumentType:
				this.detailsForm.controls.identityDocumentType.value,
			driverIdentityDocumentNumber:
				this.detailsForm.controls.identityDocumentNumber.value,
			driverIdentityDocumentIssueDate:
				this.detailsForm.controls.identityDocumentIssueDate.value,
			driverIdentityDocumentExpiryDate:
				this.detailsForm.controls.identityDocumentExpiryDate.value,
			driverIdentityDocumentIssuedByCountryId:
				this.detailsForm.controls.identityDocumentIssuedByCountryId.value,
			driverDrivingLicenses: drivingLicenses,
			driverDrivingLicenseNumber: primaryDrivingLicense?.number ?? null,
			driverDrivingLicenseCategory: primaryDrivingLicense?.category ?? null,
			driverDrivingLicenseIssueDate: primaryDrivingLicense?.issueDate ?? null,
			driverDrivingLicenseExpiryDate: primaryDrivingLicense?.expiryDate ?? null,
			driverDrivingLicenseIssuedByCountryId:
				primaryDrivingLicense?.issuedByCountryId ?? null,
		};
	}

	private _createDrivingLicenseForm(
		value: DrivingLicenseFormValue = {},
	): FormGroup {
		return this._formBuilder.group({
			id: [value.id ?? null],
			number: [value.number ?? null],
			category: [value.category ?? null],
			issueDate: [value.issueDate ?? null],
			expiryDate: [value.expiryDate ?? null],
			issuedByCountryId: [value.issuedByCountryId ?? null],
			isPrimary: [Boolean(value.isPrimary)],
		});
	}

	/**
	 * Sets the licence at the given index as primary and clears the flag
	 * on every other entry, used both for initial hydration and for
	 * promoting a replacement when the current primary is removed
	 */
	private _setPrimaryDrivingLicense(
		index: number,
		emitEvent: boolean = true,
	): void {
		this.drivingLicenses.controls.forEach((license, i) => {
			license.controls.isPrimary.setValue(i === index, {
				emitEvent,
			});
		});
	}
}
